import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import {
  SimulacaoSacocVBase,
  type TGruposParcelaBase,
} from '@/domain/simulador/factories/concrete/horizontal/sacocv/simulacao-sacocv-base'
import { CalculadorFluxoDePagamentoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/calculador-fluxo-pagamento-sacocv-parcelado'
import { CalculadorPrazoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/calculador-prazo-sacocv-parcelado'
import { GerenciadorIntermediariasPosConclusaoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/gerenciador-intermediarias-pos-conclusao-sacocv-parcelado'
import { GerenciadorIntermediariasSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/gerenciador-intermediarias-sacocv-parcelado'
import { GerenciadorMensaisSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/gerenciador-mensais-sacocv-parcelado'
import { GerenciadorPosConclusaoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/gerenciador-pos-conclusao-sacocv-parcelado'
import type {
  TSimulacaoCalculoPrazo,
  TSimulacaoCalculoRotinaArquivo,
  TSimulacaoCalculoValoresTotais,
  TSimulacaoFluxoPagamento,
} from '@/domain/simulador/factories/types/simulacao-calculo'
import { GerenciadorEntradasBase } from '@/domain/simulador/gerenciador-entradas-base'
import { GerenciadorPosConclusaoBase } from '@/domain/simulador/gerenciador-pos-conclusao-base'
import type {
  TParametrosDinamicosSimulacaoSimulador,
  TSimulacaoGrupoIntermediariasPosConclusao,
  TSimulacaoSchema,
} from '@/domain/simulador/types/simulacao.type'
import type { TGetSimulacaoDuplicarResponse } from '@/types/simulacao'
import { CalculadoraDeValoresTotaisSacocVParcelado } from '@/utils/calculadora-valores-totais-sacocv-parcelado'
import { GruposParcelasApiMapper } from '@/utils/grupos-parcelas-api.mapper'

import { RotinaArquivoSacocVParcelado } from './rotina-arquivo-sacocv-parcelado'

type TGruposParcelaParcelado = TGruposParcelaBase & {
  intermediariasPosConclusao: TSimulacaoGrupoIntermediariasPosConclusao
}

export class SimulacaoSacocVParcelado extends SimulacaoSacocVBase {
  readonly entradas = new GerenciadorEntradasBase()
  readonly mensais = new GerenciadorMensaisSacocVParcelado()
  readonly intermediarias = new GerenciadorIntermediariasSacocVParcelado()
  readonly posConclusao = new GerenciadorPosConclusaoSacocVParcelado()
  readonly intermediariasPosConclusao = new GerenciadorIntermediariasPosConclusaoSacocVParcelado()
  readonly posConclusaoFormatoSaldo = new GerenciadorPosConclusaoBase()
  readonly tipoDeAmortizacao = 'SacocV' as const
  readonly formaDePagamento = 'Parcelado' as const

  protected criarGruposParcela(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TGruposParcelaParcelado {
    const { condicaoDePreco, tabelaDePreco } = parametrosDinamicos

    const datasSinais = [
      condicaoDePreco.dataSinal1,
      condicaoDePreco.dataSinal2,
      condicaoDePreco.dataSinal3,
      condicaoDePreco.dataSinal4,
    ].filter((dataSinal): dataSinal is string => Boolean(dataSinal))

    const mensais = this.mensais.criarGrupoInicial(
      condicaoDePreco.dataPrimeiraMensal,
      condicaoDePreco.dataUltimaMensal,
      tabelaDePreco.valorBaseMensais,
      condicaoDePreco.previsaoDeEntregaDoBloco,
    )

    const entradas = this.entradas.criarGrupoInicial(datasSinais, tabelaDePreco.valorBaseSinal, {
      data: condicaoDePreco.dataAto,
      valor: tabelaDePreco.valorBaseAto,
    })
    const ultimaEntrada = entradas.at(-1)

    const grupoPosConclusaoFormatoSaldo = this.posConclusaoFormatoSaldo.deveGerarPosConclusao(
      condicaoDePreco.previsaoDeEntregaDoBloco,
      this.dataSimulacao,
    )
      ? this.posConclusaoFormatoSaldo.criarGrupo(
          tabelaDePreco.valorBaseMensais,
          condicaoDePreco.dataSaldo,
          condicaoDePreco.previsaoDeEntregaDoBloco,
          this.dataSimulacao,
        )
      : null

    const grupoPosConclusaoFormatoParcelado = this.posConclusao.criarGrupo(
      tabelaDePreco.valorBaseMensais,
      condicaoDePreco.dataSaldo,
      condicaoDePreco.previsaoDeEntregaDoBloco,
      this.dataSimulacao,
      condicaoDePreco.totalDeMesesDoParcelamentoDeMensais,
      tabelaDePreco.valorBaseSaldo,
      ultimaEntrada?.data,
    )

    const ultimaDataIntermediariaConfigurada = condicaoDePreco.dataDasIntermediarias.at(-1)

    return {
      entradas,
      mensais,
      intermediarias: this.intermediarias.criarGrupoInicial(
        condicaoDePreco.dataDasIntermediarias,
        tabelaDePreco.valorBaseIntermediarias,
        condicaoDePreco.previsaoDeEntregaDoBloco,
      ),
      intermediariasPosConclusao: this.intermediariasPosConclusao.criarGrupoInicialCompleto(
        tabelaDePreco.valorBaseSaldo,
        grupoPosConclusaoFormatoSaldo,
        grupoPosConclusaoFormatoParcelado,
        condicaoDePreco.previsaoDeEntregaDoBloco,
        ultimaDataIntermediariaConfigurada,
      ),
      posConclusao: grupoPosConclusaoFormatoParcelado,
    }
  }

  calcularRotinaArquivo(grupos: TSimulacaoCalculoRotinaArquivo, parametrosRotinaArquivo: TParametrosRotinaArquivo) {
    const rotinaArquivo = new RotinaArquivoSacocVParcelado(parametrosRotinaArquivo)
    return rotinaArquivo.calcular(grupos)
  }

  calcularValoresTotais(grupos: TSimulacaoCalculoValoresTotais, valorTabela = 0) {
    return new CalculadoraDeValoresTotaisSacocVParcelado(grupos, valorTabela)
  }

  calcularFluxoDePagamento(simulacao: TSimulacaoFluxoPagamento) {
    return new CalculadorFluxoDePagamentoSacocVParcelado(
      simulacao.entradas,
      simulacao.mensais,
      simulacao.intermediarias,
      simulacao.posConclusao,
      simulacao.intermediariasPosConclusao,
    )
  }

  calcularPrazo(simulacao: TSimulacaoCalculoPrazo) {
    return new CalculadorPrazoSacocVParcelado(simulacao)
  }

  criar(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'SacocV'> {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      id: crypto.randomUUID(),
      dataSimulacao: this.dataSimulacao,
      ...simulacaoBase,
      ...grupos,
      formaDePagamento: 'Parcelado',
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  duplicar(
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'SacocV', 'Parcelado'> {
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const { entradas, intermediarias, mensais, posConclusao, intermediariasPosConclusao } = new GruposParcelasApiMapper(
      simulacao,
    ).buildGruposParcelas()
    return {
      id: crypto.randomUUID(),
      simulacaoAnteriorId: simulacao.id,
      dataSimulacao: this.dataSimulacao,
      formaDePagamento: 'Parcelado',
      ...simulacaoBase,
      entradas,
      mensais,
      intermediarias,
      posConclusao,
      intermediariasPosConclusao: intermediariasPosConclusao ?? [],
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  recalcular(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      ...simulacaoBase,
      ...grupos,
    }
  }
}
